/**
 * ConfigKeyList — left-hand master list of the 5 reasoning config keys.
 *
 * GET /config only returns active rows, so a key absent from `configRows`
 * has no versions yet ("Not configured"). Selecting a different key while
 * the editor has unsaved changes is handled by the parent (ReasoningPromptsPage)
 * via the discard-confirm dialog — this component just reports intent.
 */

import React from 'react';
import { Box, Typography, List, ListItemButton, Alert, Button, Skeleton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StatusBadge from '../../common/StatusBadge';
import { colors, typography } from '../../../theme/designTokens';
import { ReasoningConfigKey, ReasoningConfigRow } from '../../../services/quillAdminService';
import { REASONING_CONFIG_MANIFEST } from './reasoningManifest';

export interface ConfigKeyListProps {
  configRows: ReasoningConfigRow[] | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  selectedKey: ReasoningConfigKey;
  onSelectKey: (key: ReasoningConfigKey) => void;
}

const ConfigKeyList: React.FC<ConfigKeyListProps> = ({
  configRows,
  loading,
  error,
  onRetry,
  selectedKey,
  onSelectKey,
}) => {
  return (
    <Box sx={{ width: 300, flexShrink: 0 }}>
      <Typography
        sx={{
          fontFamily: typography.fonts.heading,
          fontWeight: 700,
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          mb: 1.5,
        }}
      >
        Config Keys
      </Typography>

      {loading && (
        <Box>
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ mb: 1, borderRadius: '10px' }} />
          ))}
        </Box>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
              Retry
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <List disablePadding aria-label="Reasoning config keys">
          {REASONING_CONFIG_MANIFEST.map((entry) => {
            const row = configRows?.find((r) => r.config_key === entry.key) || null;
            const isSelected = entry.key === selectedKey;
            return (
              <ListItemButton
                key={entry.key}
                selected={isSelected}
                onClick={() => onSelectKey(entry.key)}
                sx={{
                  borderRadius: '10px',
                  mb: 1,
                  alignItems: 'flex-start',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: isSelected ? colors.deepTeal[500] : colors.gray[200],
                  backgroundColor: isSelected ? `${colors.deepTeal[500]}0D` : 'background.paper',
                  '&.Mui-selected': {
                    backgroundColor: `${colors.deepTeal[500]}0D`,
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: `${colors.deepTeal[500]}1A`,
                  },
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: colors.gray[900] }}>
                  {entry.displayName}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.75, lineHeight: 1.35 }}>
                  {entry.description}
                </Typography>
                {row ? (
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      fontFamily: typography.fonts.mono,
                      color: colors.deepTeal[500],
                    }}
                  >
                    Active v{row.version} · {row.model} · temp {row.temperature}
                  </Typography>
                ) : (
                  <StatusBadge status="warning" label="Not configured" size="sm" />
                )}
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default ConfigKeyList;
